import getShopResults from "api/requests/getShopResults";
import getSearchResults from "api/requests/getSearchResults";
import { NextPageContext } from "next";
import { Coordinates } from "custom-types/Coordinates";

import { Brand } from "custom-types/Brand";
import { Dispensary } from "custom-types/Dispensary";
import { Strain } from "custom-types/Strain";
import { MappedFilterOption } from "custom-types/Shop";
import { Query } from "custom-types/Query";
import { CustomQuery } from "hooks/useRouter";

export const MAX_DISTANCE = 30; // 30 miles

interface QueryParams {
  q?: string;
  shopCategory?: string;
  shopName?: string;
  product_category?: string;
  strain_category?: string;
  strain_name?: string;
  type?: string;
  deal_active?: string;
}

export const generateShopQuery = ({
  q,
  shopCategory,
  shopName,
  product_category,
  strain_category,
  strain_name,
  type,
  deal_active,
}: QueryParams) => {
  const query: Query = { q };

  if (shopCategory && shopName) {
    query[`filter[${shopCategory}_name]`] = [shopName];
  }

  if (product_category) {
    query["filter[product_category]"] = product_category;
  }

  if (strain_category) {
    query["filter[strain_category]"] = strain_category;
  }

  if (strain_name) {
    query["filter[strain_name]"] = strain_name;
  }

  if (type) {
    query["filter[type]"] = type;
  }

  if (deal_active) {
    query["filter[deal_active]"] = "true";
  }

  return query;
};

export type ShopState = {
  loading: boolean;
  selection?: Brand | Strain;
  dispensary: Dispensary[];
  availableFilters: MappedFilterOption[];
  totalCount?: number;
};

export type ShopResults = Omit<ShopState, "loading">;

export const initialState: ShopState = {
  loading: false,
  selection: undefined,
  dispensary: [],
  availableFilters: [],
  totalCount: 0,
};

export const SHOP_LOADING = "SHOP_LOADING";
export const SHOP_RESULTS = "SHOP_RESULTS";

export type ShopLoadingAction = {
  type: typeof SHOP_LOADING;
};

export type ShopResultsAction = {
  type: typeof SHOP_RESULTS;
  results: ShopResults;
};

export type ShopActions = ShopLoadingAction | ShopResultsAction;

export const getShopState = async (
  ctx: NextPageContext,
  coordinates: Coordinates,
  countryCode: string
) => {
  const customQuery: CustomQuery = ctx.query;
  const {
    q,
    shopCategory,
    shopName,
    product_category,
    strain_category,
    strain_name,
    deal_active,
  } = customQuery;
  const {
    store: { dispatch },
  } = ctx;

  const selectionQuery = generateShopQuery({
    shopCategory,
    shopName,
  });

  const resultsQuery = generateShopQuery({
    q,
    shopCategory,
    shopName,
    product_category,
    strain_category: strain_category as string,
    strain_name: strain_name as string,
    type: "menu_item",
    deal_active,
  });

  // Only make the request if the user has a brand or strain selected
  const selectionRequest =
    (shopCategory === "brand" || shopCategory === "strain") && shopName
      ? getSearchResults(selectionQuery, coordinates, MAX_DISTANCE, countryCode)
      : undefined;

  const [selectionResults, results] = await Promise.all([
    selectionRequest,
    getShopResults(resultsQuery, coordinates, MAX_DISTANCE, countryCode),
  ]);

  const selection = selectionResults
    ? selectionResults[shopCategory as "brand" | "strain"][0]
    : undefined;

  dispatch<ShopResultsAction>({
    type: SHOP_RESULTS,
    results: { ...results, selection },
  });

  // Forward to the brand page if no results found nearby
  if (
    shopCategory === "brand" &&
    results.totalCount === 0 &&
    ctx?.res &&
    selection?.slug
  ) {
    ctx.res.writeHead(302, {
      Location: `/brands/${selection.slug}`,
    });
    ctx.res.end();
  }
};

export const shopReducer = (
  state = initialState,
  action: ShopActions
): ShopState => {
  switch (action.type) {
    case SHOP_LOADING:
      return {
        ...state,
        loading: true,
      };
    case SHOP_RESULTS:
      return {
        ...state,
        loading: false,
        ...action.results,
      };
    default:
      return state;
  }
};

export default shopReducer;
